import { memo, FC, useState, useRef } from 'react'
import { Button, Popover } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import styles from './index.module.scss'
import WalletButton from '@/components/WalletButton'
import { ReactComponent as AddCoinIcon } from '@/assets/icons/addCoin.svg'
import { WalletItemModel } from '@/services/apis/wallet/models'
import { WalletCoin } from '@/services/apis/coin/models'
import CoinLogo from '../CoinLogo'
import CoinManagerModal from '../CoinManagerModal'
import ReceiveModal from '../ReceiveModal'
import { useTranslation } from '@/services/i18n'
import { CURRENCY } from '@/constants'
import { useCanReceive, useCanSend } from '@/hooks/permission'
import SendDrawer from '../SendDrawer'
import { useSelectWorkspaceUserInfo } from '@/hooks/user'
import { useCheckBackupStatus } from '@/hooks/user'
import BackupModal from '@/components/BackupModal'
import Table from '@/components/Table'
import { formatUSD } from '@/utils/format'
import { coinTypeEnum } from '@/constants/enum'
interface CoinListProps {
  data?: WalletItemModel
  isLoading: boolean
}

interface ContentProps {
  item: WalletCoin
}

const Content: FC<ContentProps> = ({ item }) => {
  const { t } = useTranslation()
  // 隐藏所有地址
  function handleAddress(address: string) {
    let fontAddress = address.slice(0, 6)
    let behindAddress = address.slice(address.length - 6, address.length)
    return `${fontAddress}...${behindAddress}`
  }
  return (
    <div className={styles.content}>
      <div className={styles.row}>
        <div className={styles.left}>{t('record.type')}:</div>
        <div className={styles.right}>
          {item.coinTypeEnum === coinTypeEnum.MASTER ? t('record.master') : 'TOKEN'}
        </div>
      </div>
      <div className={styles.row}>
        <div className={styles.left}>{t('record.contractAddress')}:</div>
        <div className={styles.right}>
          {item?.tokenIdentifier ? (
            <span style={{ color: '#00C59A' }}>{handleAddress(item?.tokenIdentifier)}</span>
          ) : (
            t('common.none')
          )}
        </div>
      </div>
      <div className={styles.row}>
        <div className={styles.left}>{t('record.accuracy')}:</div>
        <div className={styles.right}>{item.showCoinDecimal}</div>
      </div>
      <div className={styles.row}>
        <div className={styles.left}>{t('record.coinKey')}:</div>
        <div className={styles.right}>{item.coinKey}</div>
      </div>
    </div>
  )
}

const CoinList: FC<CoinListProps> = ({ data, isLoading }) => {
  const { t } = useTranslation()
  const canSend = useCanSend()
  const canReceive = useCanReceive()
  const user = useSelectWorkspaceUserInfo()
  const checkBackupStatus = useCheckBackupStatus()
  const [isOpenSendDrawer, setOpenSendDrawerStatus] = useState(false)
  const [coinKey, setCoinKey] = useState('')
  const isChecking = useRef<boolean>(false)

  const columns: ColumnsType<WalletCoin> = [
    {
      title: t('common.currency'),
      key: 'coinName',
      className: styles.firstColumn,
      render: (_: any, item: WalletCoin) => {
        return (
          <div className={styles.coinNameWrapper}>
            <CoinLogo logo={item.logoUrl} networkLogo={item.networkLogo} />
            <Popover content={<Content item={item} />} trigger="hover" arrow={false}>
              <div className={styles.coinName}>{`${item.coinName} (${item.coinFullName})`}</div>
            </Popover>
          </div>
        )
      },
    },
    // 所属网络
    {
      title: t('common.affiliatedNetwork'),
      key: 'networkName',
      render: (_: any, item: WalletCoin) => {
        return <div>{item.networkName}</div>
      },
    },
    {
      title: t('common.num'),
      key: 'balance',
      render: (_: any, item: WalletCoin) => {
        if (!item) {
          return '0'
        }
        return formatUSD(item.balance, item.showCoinDecimal)
      },
    },
    {
      title: CURRENCY,
      key: 'usdBalance',
      render: (_: any, item: WalletCoin) => {
        if (!item) {
          return '0.00'
        }
        return `≈${formatUSD(item.usdBalance)}`
      },
    },
    {
      title: t('common.operation'),
      key: 'operation',
      render: (_: any, item: WalletCoin) => (
        <div className={styles.operation}>
          {canSend && (
            <Button
              type="link"
              onClick={async () => {
                if (isChecking.current) {
                  return
                }
                isChecking.current = true
                const result = await checkBackupStatus()
                if (result) {
                  setOpenSendDrawerStatus(true)
                  setCoinKey(item.coinKey)
                } else {
                  BackupModal.open(user?.role)
                }
                isChecking.current = false
              }}
            >
              {t('common.send')}
            </Button>
          )}
          {canReceive && (
            <Button
              type="link"
              onClick={async () => {
                if (isChecking.current) {
                  return
                }
                isChecking.current = true
                const result = await checkBackupStatus()
                if (result) {
                  ReceiveModal.open(item, data!)
                } else {
                  BackupModal.open(user?.role)
                }
                isChecking.current = false
              }}
            >
              {t('common.receive')}
            </Button>
          )}
        </div>
      ),
    },
  ]
  const onClick = () => {
    data && CoinManagerModal.open(data!.accountKey)
  }
  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h3>{t('wallet.detail.tokens')}</h3>
        <WalletButton
          text={t('wallet.detail.coinManage')}
          icon={<AddCoinIcon fill="#C7D2DD" />}
          onClick={onClick}
        />
      </div>
      <div className={styles.tableWrapper}>
        <Table
          columns={columns}
          dataSource={data?.coinList}
          rowKey={item => item.coinKey}
          loading={isLoading}
          pagination={false}
        />
      </div>
      <SendDrawer
        open={isOpenSendDrawer}
        onClose={() => setOpenSendDrawerStatus(false)}
        coinKey={coinKey}
        onSelectCoin={setCoinKey}
        coins={data?.coinList ?? []}
      />
    </div>
  )
}

export default memo(CoinList)
